import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Title,
  ActionIcon,
  Select,
  Paper,
  Center,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconPlus } from "../../shared/svg/AddIcon";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import {
  getLanguageCode,
  getLanguageName,
  LANGUAGE_CHOICES,
} from "../../utils/helpers";
import { useMediaQuery } from "@mantine/hooks";
import {
  addLanguage,
  deleteLanguage,
  editLanguage,
  fetchLanguages,
} from "../../api/profileEditingApi";
import { LanguagesEdit } from "../../types/profile-editing";
import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { CheckIcon } from "../../shared/svg/CheckIcon";
import { notifications } from "@mantine/notifications";
import { TrashCanSmall } from "../../shared/svg/TrashCanSmall";
import ConfirmDelete from "../../utils/components/ConfirmDelete";
import useProfileStore from "../../store/useProfileStore";

interface Props {
  setChanges: (t: boolean) => void;
}

const LanguagesEditingWidget: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<number | null>(null);

  const matches = useMediaQuery("(max-width: 840px)");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [languages, setLanguages] = useState<LanguagesEdit[]>([]);
  const [newLanguage, setNewLanguage] = useState<string | null>(null);
  const [newLanguageLevel, setNewLanguageLevel] = useState<string | null>("00");
  const [showInput, setShowInput] = useState<boolean>(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { setUserMarketChanges } = useProfileStore();

  useEffect(() => {
    const foo = async () => {
      try {
        const res = await fetchLanguages();
        setLanguages(res);
      } catch (error) {
        console.log(error);
      }
    };

    foo();
  }, []);

  const handleAddLanguage = async () => {
    setErrorMessage(null);
    if (newLanguage && newLanguageLevel) {
      try {
        const res = await addLanguage({
          language: newLanguage,
          proficiency: newLanguageLevel,
        });
        notifications.show({
          title: t("success"),
          message: t("add_successfully"),
          icon: <CheckIcon />,
          color: "green",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
        setLanguages([res, ...languages]);
        setChanges(true);
        setUserMarketChanges(true);
        setNewLanguage(null);
        setNewLanguageLevel("00");
        setShowInput(false);
      } catch (error: any) {
        if (error.errors.non_field_errors) {
          setErrorMessage(error.errors.non_field_errors[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };

  const handleRemoveLanguage = async (id: number) => {
    setErrorMessage(null);
    setLanguages((prev) => prev.filter((language, i) => id !== language.id));
    notifications.show({
      title: t("success"),
      message: t("delete_successfully"),
      icon: <TrashCanSmall />,
      color: "#d3d3d3",
      style: {
        border: "1px solid #d3d3d3",
        borderRadius: "8px",
      },
    });
    setChanges(true);
    setUserMarketChanges(true);
    if (id) {
      try {
        await deleteLanguage(id);
      } catch (error: any) {
        if (error.errors.non_field_errors) {
          setErrorMessage(error.errors.non_field_errors[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };

  const handleUpdateLanguage = async () => {
    setErrorMessage(null);
    if (editingId !== null && newLanguage && newLanguageLevel) {
      try {
        const updated = await editLanguage({
          id: editingId,
          language: newLanguage,
          proficiency: newLanguageLevel,
        });
        notifications.show({
          title: t("success"),
          message: t("changed_successfully"),
          icon: <EditIconSmall />,
          color: "#CEB7FF",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
        setLanguages((prev) =>
          prev.map((lang) => (lang.id === editingId ? updated : lang))
        );

        setChanges(true);
        setUserMarketChanges(true);
        setEditingId(null);
        setNewLanguage(null);
        setNewLanguageLevel("00");
        setShowInput(false);
      } catch (error: any) {
        if (error.errors.non_field_errors) {
          setErrorMessage(error.errors.non_field_errors[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };

  const renderLanguageCard = (language: any, index: number) => (
    <Paper
      key={language.id}
      radius="md"
      withBorder
      style={{
        width: matches ? "100%" : "calc(33% - 14px)",
        minWidth: 220,
        backgroundColor: "rgb(245, 243, 250)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 8,

        padding: "32px 16px 16px 16px",
      }}
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        bg="transparent"
        onClick={() => {
          setEditingId(language.id);
          setNewLanguage(getLanguageCode(language.language));
          setNewLanguageLevel(language.proficiency);
          setShowInput(true);
          setErrorMessage(null);
        }}
        style={{
          position: "absolute",
          top: 8,
          right: 38,
          backgroundColor: "#f2f2f2",
          touchAction: "manipulation",
          pointerEvents: "auto",
          zIndex: 10,
        }}
        title={t("edit")}
      >
        <EditIconSmall />
      </ActionIcon>

      <ActionIcon
        variant="subtle"
        color="gray"
        bg="transparent"
        onClick={() => {
          setIsDeleteOpen(true);
          setDeleteId(language.id);
        }}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          backgroundColor: "#f2f2f2",
          touchAction: "manipulation",
          pointerEvents: "auto",
          zIndex: 10,
        }}
        title={t("remove")}
      >
        <ClearIcon />
      </ActionIcon>
      <Text size="md" fw={500}>
        {t("language")}:{" "}
        <span style={{ fontWeight: 600 }}>
          {getLanguageName(language.language)}
        </span>
      </Text>

      <Text size="md">
        {t("proficiency_level")}:{" "}
        <span style={{ fontWeight: 600, color: "#333" }}>
          {language.proficiency === "00"
            ? t("no_proficiency")
            : language.proficiency}
        </span>
      </Text>
    </Paper>
  );

  const renderInputCard = () => (
    <Paper
      withBorder
      radius="md"
      p="md"
      style={{
        width: 470,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        paddingBottom: 16,
        boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
      }}
    >
      <Flex justify="space-between" align="center">
        <Text size="sm" fw={500} color="gray.7">
          {t("add_language")}
        </Text>
        <ActionIcon
          variant="transparent"
          color="gray"
          onClick={() => {
            setShowInput(false);
            setNewLanguage(null);
            setNewLanguageLevel("00");
            setEditingId(null);
            setErrorMessage(null);
          }}
          title={t("cancel") || "Cancel"}
        >
          <ClearIcon />
        </ActionIcon>
      </Flex>

      <Flex
        gap="sm"
        align="center"
        justify={matches ? "center" : undefined}
        direction={matches ? "column" : "row"}
      >
        <Select
          placeholder={t("select_language_skill")}
          data={LANGUAGE_CHOICES.map(([value, label]) => ({
            value,
            label,
          }))}
          value={newLanguage}
          onChange={setNewLanguage}
          size="sm"
          radius="md"
          style={{ flex: 1 }}
          searchable
        />

        <Select
          placeholder={t("select_proficiency")}
          data={[
            { value: "00", label: t("no_proficiency") },
            { value: "A1", label: t("level_a1") },
            { value: "A2", label: t("level_a2") },
            { value: "B1", label: t("level_b1") },
            { value: "B2", label: t("level_b2") },
            { value: "C1", label: t("level_c1") },
            { value: "C2", label: t("level_c2") },
          ]}
          value={newLanguageLevel}
          onChange={setNewLanguageLevel}
          size="sm"
          radius="md"
          style={{ flex: 1 }}
        />
        <Button
          variant="filled"
          size="sm"
          onClick={editingId ? handleUpdateLanguage : handleAddLanguage}
          disabled={!newLanguage || !newLanguageLevel}
          leftSection={editingId ? <CheckIcon /> : <IconPlus />}
          style={{ flexShrink: 0 }}
        >
          {editingId ? t("save") : t("add")}
        </Button>
      </Flex>
      {errorMessage && (
        <Text color="#B00020" size="xs" mt={4}>
          {errorMessage}
        </Text>
      )}
    </Paper>
  );

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        direction={matches ? "column" : "row"}
        mb="md"
        gap={matches ? 20 : 8}
      >
        <Title size={32} fw={600}>
          {t("your_languages")}
        </Title>

        <Button
          style={{ flexShrink: 0 }}
          size="md"
          leftSection={<IconPlus />}
          onClick={() => {
            setErrorMessage(null);
            setShowInput(true);
          }}
          disabled={showInput}
          variant="light"
          radius="md"
        >
          {t("add_language")}
        </Button>
      </Flex>

      <Flex wrap="wrap" gap="lg">
        {showInput && editingId === null && renderInputCard()}
        {languages.map((lang, index) =>
          editingId === lang.id
            ? renderInputCard()
            : renderLanguageCard(lang, index)
        )}

        {!showInput && languages.length === 0 && (
          <Center py="xl">
            <Paper
              radius="md"
              p="xl"
              withBorder
              shadow="sm"
              style={{ maxWidth: 1000, width: "100%" }}
            >
              <Flex
                align="center"
                w="100%"
                px={20}
                gap={matches ? 20 : 40}
                direction={matches ? "column" : "row"}
              >
                <Title
                  order={2}
                  fw={700}
                  w={matches ? "100%" : "33%"}
                  ta="left"
                  style={{ fontSize: "1.5rem" }}
                >
                  {t("nothing_here")}
                </Title>
                <Text
                  size="xl"
                  c="dimmed"
                  w={matches ? "100%" : "66%"}
                  style={{ fontSize: "1.125rem" }}
                >
                  {t("no_languages")}
                </Text>
              </Flex>
            </Paper>
          </Center>
        )}
      </Flex>
      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onDelete={() => {
          if (deleteId !== null) handleRemoveLanguage(deleteId);
        }}
        title={t("delete_language_title")}
      />
    </Box>
  );
};

export default LanguagesEditingWidget;
